import { inject, Injectable } from '@angular/core';
import { IpbLocalStorageService } from '@ipb-genesis/ipb-services';
import { RolesEnum } from '../../../enums/ipb-roles.enum';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { IUnits, TokenData } from '../../../interfaces';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private ipbLocalStorage: IpbLocalStorageService = inject(IpbLocalStorageService);
	private _accessToken$ = new BehaviorSubject<string>(this.ipbLocalStorage.get('token') || '');
	#roles$ = new BehaviorSubject<RolesEnum[]>([]);
	#returnUrl?: string;

	public get returnUrl() {
		return this.#returnUrl;
	}

	public get roles$() {
		return this.#roles$.pipe(
			map((roles) => {
				return roles.map(
					(role) => role?.replace('_RO', '') as RolesEnum,
				);
			}),
		);
	}

	public data: { roles: RolesEnum[] } = { roles: [] };
	public set setRoles(v: RolesEnum[]) {
		this.#roles$.next(v);
		this.data.roles = v;
	}

	public get roles(): RolesEnum[] {
		return this.data.roles;
	}

	get accessToken$() {
		return this._accessToken$.asObservable();
	}

	public constructor(
		public router: Router,
		private ipbLocalStorageService: IpbLocalStorageService,
	) {
	}

	public isAuthenticated(): boolean {
		const token = this.getTokenData();
		const tokenExpired = token?.expirationDate ? token.expirationDate < new Date() : true;

		return !tokenExpired;
	}

	public checkRole(rolesToCheck: RolesEnum[]): boolean {
		return this.roles.some((role) => rolesToCheck.includes(role));
	}

	public checkRoleAsync(rolesToCheck: RolesEnum[]): Promise<boolean> {
		return firstValueFrom(
			this.roles$.pipe(
				filter((roles) => roles.length > 0),
				map((roles) => roles.some((role) => rolesToCheck.includes(role))),
			),
			{ defaultValue: false },
		);
	}

	public setReturnUrl(url: string) {
		// Paginas pre-login e a home não são returnUrls validas,
		// - porque depois do login deve ir para area logada
		// - porque o redirect to home é definido pela role do usuario
		const excludedUrls = ['/login', '/forgot-password', '/reset-password', '/home', '/'];
		const urlWithoutQueryParams = url.split('?')[0];

		if (!excludedUrls.includes(urlWithoutQueryParams) ) {
			this.#returnUrl = url;
		}
	}

	public redirectToHome(force?: boolean) {
		let _force = force;

		let homeRoute = '/home';
		if (this.roles.map(e => e.replace('_RO', '')).includes(RolesEnum.INFORMATION_SECURITY)) {
			homeRoute = '/internal-users-mfe';
		}
		if (this.#returnUrl) {
			_force = true;
			homeRoute = this.#returnUrl;
			this.setReturnUrl(''); // Clear return URL after using it
		}

		if (_force) window.location.href = homeRoute;
		else this.router.navigate([homeRoute]);
	}

	public getAuthToken(): string {
		this.ipbLocalStorage.get('token')
		return this.ipbLocalStorage.get('token');
	}

	public getUserUnit(): string {
		return this.ipbLocalStorage.get('unit');
	}

	public getUserUnitLabel(): string {
		const unitListJson = this.ipbLocalStorage.get('unitLists');
		if (!unitListJson) return '-';
		try {
			const units = JSON.parse(unitListJson) as IUnits[];
			const parsedUnits = units?.map((unit) => ({ label: unit.name, value: unit.id })) ?? [];
			return parsedUnits.find((unit) => unit.value === Number(this.getUserUnit()))!.label;
		} catch (error) {
			console.warn(error);

			return '-';
		}
	}

	public setAuthToken(token: string) {
		this._accessToken$.next(token);
		this.ipbLocalStorage.set('token', token);
	}

	public setUserUnit(token: string) {
		this.ipbLocalStorage.set('unit', token);
	}

	public deleteAuthToken() {
		this.ipbLocalStorage.clear();
		window?.sessionStorage?.clear?.();
	}

	public getTokenData(): TokenData | null {
		const helper = new JwtHelperService();
		const token = this.ipbLocalStorageService.get('token');
		const unit = this.getUserUnit();
		try {
			if (!token || token == '') return null;
			const data = helper.decodeToken(token);
			const expirationDate = helper.getTokenExpirationDate(token);
			const isExpired = helper.isTokenExpired(token);
			return { ...data, userId: data?.sub, isExpired, expirationDate, raw: token, unit };
		} catch (error) {
			return null;
		}
	}

	public get isReadOnlyUser(): boolean {
		return this.getTokenData()?.readonly === 'true';
	}
}
